import React from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/**
 * Small helper wrapping @dnd-kit for a vertical or grid sortable list.
 *
 * Usage:
 *   <SortableList items={items} onReorder={(newIds) => save(newIds)}>
 *     {(item, dragHandle) => (
 *       <div>
 *         {dragHandle}
 *         ... your row content ...
 *       </div>
 *     )}
 *   </SortableList>
 */
export function SortableList({ items, onReorder, children, strategy = "vertical", getId = (i) => i.id }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => getId(i) === active.id);
    const newIndex = items.findIndex((i) => getId(i) === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex);
    onReorder(next.map(getId), next);
  };

  const strat = strategy === "grid" ? rectSortingStrategy : verticalListSortingStrategy;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map(getId)} strategy={strat}>
        {items.map((item) => (
          <SortableItem key={getId(item)} id={getId(item)}>
            {(handle) => children(item, handle)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const handle = (
    <button
      {...attributes}
      {...listeners}
      data-testid={`drag-handle-${id}`}
      type="button"
      aria-label="Drag to reorder"
      className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-primary p-2 -ml-2 touch-none"
    >
      <GripVertical size={16} strokeWidth={1.3} />
    </button>
  );
  return (
    <div ref={setNodeRef} style={style}>
      {children(handle)}
    </div>
  );
}
